import React, { useState, useRef, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./App.css";
import { Analytics } from "@vercel/analytics/react";
import Blog from "./pages/Blog/Blog";
import BlogPost from "./pages/BlogPost/BlogPost";
import About from "./components/About";
import ChatbotTest from "./pages/ChatbotTest/ChatbotTest";
import Login from "./components/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import Dashboard from "./pages/Dashboard/Dashboard";
import ProtectedResource from "./pages/Resources/ProtectedResource";
import Services from "./pages/Services/Services";

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

function AppContent() {
  return (
    <>
      <NavbarWithAuth />
      <div className="container">
        <Routes>
          {/* Home Page Route */}
          <Route
            path="/"
            element={
              <>
                {/* Hero Section */}
                <section className="hero-section">
                  <div className="hero-content">
                    <div className="hero-text">
                      <h1>AI-Powered Enterprise Automation</h1>
                      <p className="hero-subtitle">
                        Leveraging cutting-edge AI technology to revolutionize
                        enterprise workflows with military-grade precision and
                        unmatched scalability.
                      </p>
                      <div className="hero-cta">
                        <a href="#learn-more" className="primary-btn">
                          Learn More
                        </a>
                        <a
                          href="https://docs.google.com/forms/d/e/1FAIpQLSdApBm3K4OTYAf5tr4Wn54muxlJjGHronvYFCxwHja9WOODtQ/viewform?usp=dialog"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="secondary-btn"
                        >
                          Join Waitlist
                        </a>
                      </div>
                    </div>
                    <div className="hero-visual">
                      <img
                        src="/agentsamurai.jpg"
                        alt="Agent Samurai Platform"
                        className="hero-image"
                      />
                    </div>
                  </div>
                </section>

                {/* Features Section */}
                <div className="features-container">
                  <h2>Your AI Research & Automation Assistant</h2>
                  <div className="features-grid">
                    <div className="feature-card">
                      <div className="feature-icon">📊</div>
                      <h3>Advanced Analytics</h3>
                      <p>
                        Enterprise-grade financial analysis powered by
                        state-of-the-art machine learning models, delivering
                        actionable insights in real-time.
                      </p>
                    </div>
                    <div className="feature-card">
                      <div className="feature-icon">🔄</div>
                      <h3>Automated Workflow</h3>
                      <p>
                        Seamlessly integrate AI-powered automation into your
                        existing business processes with our enterprise-ready
                        API infrastructure.
                      </p>
                    </div>
                    <div className="feature-card">
                      <div className="feature-icon">🛡️</div>
                      <h3>Enterprise Security</h3>
                      <p>
                        Bank-grade security protocols and compliance measures
                        ensuring your data remains protected at all times.
                      </p>
                    </div>
                  </div>
                </div>

                {/* How It Works Section */}
                <div className="how-it-works">
                  <h2>How Agent Samur.ai Works</h2>
                  <div className="steps-container">
                    <div className="step">
                      <div className="step-number">1</div>
                      <h3>Define Your Objective</h3>
                      <p>
                        Specify your business requirements through our intuitive
                        interface - from market research to data analysis.
                      </p>
                    </div>
                    <div className="step">
                      <div className="step-number">2</div>
                      <h3>AI-Powered Execution</h3>
                      <p>
                        Our enterprise-grade AI agents process your request with
                        precision, leveraging state-of-the-art algorithms.
                      </p>
                    </div>
                    <div className="step">
                      <div className="step-number">3</div>
                      <h3>Actionable Results</h3>
                      <p>
                        Receive comprehensive reports and insights ready for
                        implementation in your business workflow.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Call to Action */}
                {/* <div className="button-container">
                <p className="waitlist-incentive">
                  🔥 First 50 users get a free financial research report
                  ($99 value)
                </p>
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSdApBm3K4OTYAf5tr4Wn54muxlJjGHronvYFCxwHja9WOODtQ/viewform?usp=dialog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="waitlist-button"
                >
                  Join Waitlist
                </a>
              </div> */}
              </>
            }
          />

          <Route path="/about" element={<About />} />

          {/* Protected Route for the AI Assistant */}
          <Route
            path="/test"
            element={
              <ProtectedRoute>
                <ChatbotTest />
              </ProtectedRoute>
            }
          />

          {/* Login Route */}
          <Route path="/login" element={<Login />} />

          {/* Blog Routes */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<BlogPost />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/services" element={<Services />} />
          <Route path="/resources" element={<ProtectedResource />} />
        </Routes>

        {/* Footer - Moved inside container */}
        <footer className="site-footer">
          <div className="footer-container">
            <div className="footer-column">
              <h3 className="footer-title">Agent Samur.ai</h3>
              <p className="footer-description">
                AI-powered enterprise automation with military-grade precision
                and unmatched scalability.
              </p>
            </div>

            <div className="footer-column">
              <h3 className="footer-title">Company</h3>
              <ul className="footer-links">
                <li>
                  <a href="/about">About</a>
                </li>
                <li>
                  <a href="/services">Services</a>
                </li>
                <li>
                  <a href="/blog">Blog</a>
                </li>
                <li>
                  <a href="/contact">Contact</a>
                </li>
              </ul>
            </div>

            <div className="footer-column">
              <h3 className="footer-title">Legal</h3>
              <ul className="footer-links">
                <li>
                  <a href="/privacy">Privacy Policy</a>
                </li>
                <li>
                  <a href="/terms">Terms of Service</a>
                </li>
              </ul>
            </div>

            <div className="footer-column">
              <h3 className="footer-title">Connect</h3>
              <div className="social-icons">
                <a
                  href="https://twitter.com/yourprofile"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Twitter"
                >
                  <i className="fab fa-twitter"></i>
                </a>
                <a
                  href="https://linkedin.com/in/yourprofile"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <i className="fab fa-linkedin-in"></i>
                </a>
                <a
                  href="https://github.com/yourprofile"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                >
                  <i className="fab fa-github"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              &copy; {new Date().getFullYear()} AgentSamur.ai. All rights
              reserved.
            </p>
            <p>
              Built by{" "}
              <a
                href="https://barakahbridge.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                BarakahBridge
              </a>
            </p>
          </div>
        </footer>
      </div>
      <Analytics />
    </>
  );
}

// Updated NavbarWithAuth function in App.js

function NavbarWithAuth() {
  const [menuActive, setMenuActive] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const toggleMenu = () => {
    setMenuActive(!menuActive);
  };

  const closeMenu = () => {
    setMenuActive(false);
  };

  const handleClickOutside = (event) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target) &&
      menuActive
    ) {
      closeMenu();
    }
  };

  useEffect(() => {
    // Add event listener when the menu is active
    if (menuActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuActive]);

  const handleLogout = () => {
    logout();
    navigate("/");
    closeMenu(); // Close menu after logout
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="logo-container" onClick={closeMenu}>
          <img
            src="/agentsamurailogo.png"
            alt="AgentSamur.ai Logo"
            className="logo-image"
          />
        </Link>
        <div className="menu-icon" onClick={toggleMenu}>
          ☰
        </div>
        <ul ref={menuRef} className={`nav-menu ${menuActive ? "active" : ""}`}>
          <li>
            <Link to="/about" onClick={closeMenu}>
              About
            </Link>
          </li>
          <li>
            <Link to="/services" onClick={closeMenu}>
              Services
            </Link>
          </li>
          <li>
            <Link to="/resources" onClick={closeMenu}>
              Resources
            </Link>
          </li>
          <li>
            <Link to="/blog" onClick={closeMenu}>
              Blog
            </Link>
          </li>
          <li>
            <Link to="/contact" onClick={closeMenu}>
              Contact
            </Link>
          </li>
          {currentUser && (
            <>
              <li>
                <Link to="/dashboard" onClick={closeMenu}>
                  Dashboard
                </Link>
              </li>
              <li>
                <Link to="/test" onClick={closeMenu}>
                  AI Assistant
                </Link>
              </li>
            </>
          )}
          <li className="auth-item">
            {currentUser ? (
              <button onClick={handleLogout} className="logout-button">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 17L21 12L16 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12H9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Logout</span>
              </button>
            ) : (
              <Link to="/login" onClick={closeMenu} className="login-button">
                Login
              </Link>
            )}
          </li>
        </ul>
      </div>
    </nav>
  );
}

export default App;
